import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/homeData';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于糖心视频",
            "description": aboutInfo.introduction,
            "url": "https://tangxin.com/about",
            "about": {
              "@type": "Organization",
              "name": "糖心视频",
              "description": aboutInfo.introduction,
              "foundingDate": "2020",
              "url": "https://tangxin.com",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": aboutInfo.contact.phone,
                "email": aboutInfo.contact.email,
                "contactType": "customer service",
                "availableLanguage": "Chinese"
              },
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "建国路88号糖心大厦",
                "addressLocality": "北京",
                "addressRegion": "朝阳区",
                "addressCountry": "CN"
              }
            }
          })
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <section className="mt-10 text-center max-w-3xl mx-auto mb-16">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
              关于{aboutInfo.brandName}
            </h1>
            <p className="text-foreground-500 text-lg mb-6">{aboutInfo.tagline}</p>
            <p className="text-foreground-600 leading-relaxed text-[15px]">{aboutInfo.introduction}</p>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">核心价值观</h2>
              <p className="text-sm text-foreground-500">指导我们前进的信念与准则</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.values.map((value, index) => (
                <div key={index} className="bg-background-100 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-500 rounded-2xl mx-auto mb-4">
                    <span className="text-background-50 font-heading font-bold text-xl">{index + 1}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">发展历程</h2>
              <p className="text-sm text-foreground-500">从创立至今的重要里程碑</p>
            </div>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-px bg-background-200"></div>
                <div className="space-y-8">
                  {aboutInfo.milestones.map((milestone, index) => (
                    <div key={index} className="relative flex items-start gap-6 pl-20">
                      <div className="absolute left-6 w-5 h-5 flex items-center justify-center bg-primary-500 rounded-full ring-4 ring-background-50">
                        <div className="w-1.5 h-1.5 bg-background-50 rounded-full"></div>
                      </div>
                      <div className="bg-background-100 rounded-xl p-5 flex-1">
                        <span className="inline-block bg-primary-500 text-background-50 text-xs font-bold px-3 py-1 rounded-full mb-2">
                          {milestone.year}
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed">{milestone.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">用户口碑</h2>
              <p className="text-sm text-foreground-500">来自真实用户的声音</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.testimonials.map((t, index) => (
                <div key={index} className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">"{t.content}"</p>
                  <div>
                    <div className="font-heading font-semibold text-sm text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-background-100 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-500">期待您的反馈与建议</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-3">
                  <i className="ri-mail-line text-background-50 text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-3">
                  <i className="ri-phone-line text-background-50 text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">客服电话</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-3">
                  <i className="ri-wechat-line text-background-50 text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
              </div>
              <div className="text-center p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-3">
                  <i className="ri-map-pin-line text-background-50 text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}